/**
 * VMSMan API Client
 *
 * TypeScript client for the REST API
 */

import { formatBytes } from '../utils.js';
import type {
  ApiError,
  Vm,
  VmListItem,
  VmCreateRequest,
  VmUpdateRequest,
  VmStatusResponse,
  VmVncInfo,
  VmGuestVncInfo,
  VmRdpInfo,
  VmMonitorInfo,
  HostMetrics,
  MonitorEventsResponse,
  IsoImage,
  DiskInfo,
  StoragePool,
  PhysicalDrive,
  CreateDiskRequest,
  LoginRequest,
  LoginResponse,
  TotpSetupResponse,
  Verify2faRequest,
  Verify2faResponse,
  UserInfo,
  DeviceInfo,
  User,
  ListUsersResponse,
  CreateUserRequest,
  UpdateUserRequest,
  ChangePasswordRequest,
  GenerateApiKeyResponse,
  ApiOptions,
  HostInfo,
  VmSchemaAttribute,
  BridgeDetail,
  BridgeSambaConfig,
  NetworkDetail,
  CreateNetworkRequest,
  RemoteHost,
  HostsListResponse,
  RemoteHostRequest,
  HostTestResult,
  Organization,
  OrganizationsListResponse,
  CreateOrganizationRequest,
  VmTemplate,
  TemplatesListResponse,
  PortForwardInfo,
  CreatePortForwardRequest,
} from './types.js';

// =============================================================================
// Client Configuration
// =============================================================================

export interface ApiClientConfig {
  baseUrl?: string;
  onAuthError?: () => void;
}

// =============================================================================
// API Client Class
// =============================================================================

export class ApiClient {
  private baseUrl: string;
  private sessionToken: string | null = null;
  private onAuthError?: () => void;

  constructor(config: ApiClientConfig = {}) {
    this.baseUrl = config.baseUrl ?? '/api';
    this.onAuthError = config.onAuthError;
    this.loadToken();
  }

  // ---------------------------------------------------------------------------
  // Token Management
  // ---------------------------------------------------------------------------

  private loadToken(): void {
    this.sessionToken = localStorage.getItem('vmsman_session_token');
  }

  private saveToken(token: string | null): void {
    this.sessionToken = token;
    if (token) {
      localStorage.setItem('vmsman_session_token', token);
    } else {
      localStorage.removeItem('vmsman_session_token');
    }
  }

  public getToken(): string | null {
    return this.sessionToken;
  }

  public isAuthenticated(): boolean {
    return this.sessionToken !== null;
  }

  /**
   * Build an authenticated WebSocket URL.
   * Browser WebSocket API cannot set custom headers, so the session token
   * is passed as a query parameter instead.
   */
  public buildWsUrl(path: string): string {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = new URL(`${proto}//${location.host}${path}`);
    if (this.sessionToken) {
      url.searchParams.set('token', this.sessionToken);
    }
    return url.toString();
  }

  // ---------------------------------------------------------------------------
  // Network Activity Indicator
  // ---------------------------------------------------------------------------

  private activeRequests = 0;

  private updateNetworkIndicator(): void {
    let el = document.getElementById('network-activity');
    if (!el) {
      el = document.createElement('div');
      el.id = 'network-activity';
      el.className = 'network-activity-indicator';
      document.body.appendChild(el);
    }
    el.style.display = this.activeRequests > 0 ? 'block' : 'none';
  }

  // ---------------------------------------------------------------------------
  // HTTP Request Helper
  // ---------------------------------------------------------------------------

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    query?: Record<string, string | number | boolean | undefined>
  ): Promise<T> {
    this.activeRequests++;
    this.updateNetworkIndicator();
    try {
    // Check for preloaded fetch (fired from inline <script> in <head>)
    let response: Response;
    const preloadKey = path.replace(/^\//, '');
    const preloads = (window as unknown as Record<string, unknown>).__preload as
      Record<string, Promise<Response>> | undefined;
    if (method === 'GET' && !body && !query && preloads && preloadKey in preloads) {
      const p = preloads[preloadKey];
      delete preloads[preloadKey];
      response = await p;
    } else {
    const headers: HeadersInit = {
      'Accept': 'application/json',
    };

    if (this.sessionToken) {
      headers['Authorization'] = `Bearer ${this.sessionToken}`;
    }

    let url = `${this.baseUrl}${path}`;

    // Add query parameters
    if (query) {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(query)) {
        if (value !== undefined) {
          params.set(key, String(value));
        }
      }
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const options: RequestInit = {
      method,
      headers,
    };

    if (body !== undefined && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }

    response = await fetch(url, options);
    }

    // Handle auth errors
    if (response.status === 401) {
      this.saveToken(null);
      this.onAuthError?.();
      throw new ApiClientError('UNAUTHORIZED', 'Not authenticated', 401);
    }

    // Parse response
    const contentType = response.headers.get('content-type');
    let data: unknown = null;
    if (contentType?.includes('application/json')) {
      data = await response.json();
    }

    if (!response.ok) {
      const body = data as Record<string, unknown> | null;
      let code = 'API_ERROR';
      let message = `HTTP ${response.status}`;
      let details: Record<string, unknown> | undefined;

      if (body) {
        if (typeof body.error === 'string') {
          // Flat format: { error: "message", code: "CODE" }
          message = body.error;
          if (typeof body.code === 'string') code = body.code;
        } else if (typeof body.error === 'object' && body.error !== null) {
          // Nested format: { error: { code, message, details } }
          const err = body.error as ApiError;
          code = err.code ?? code;
          message = err.message ?? message;
          details = err.details;
        } else if (typeof body.message === 'string') {
          message = body.message;
        }
      }

      throw new ApiClientError(code, message, response.status, details);
    }

    return data as T;
    } finally {
      this.activeRequests--;
      this.updateNetworkIndicator();
    }
  }

  // ---------------------------------------------------------------------------
  // VM Endpoints
  // ---------------------------------------------------------------------------

  /** List all VMs (status defaults to 'stopped' until WebSocket updates) */
  async listVms(): Promise<VmListItem[]> {
    const result = await this.request<{ vms: Vm[] }>('GET', '/vms');
    return result.vms.map(vm => ({ ...vm, status: 'unknown' as const }));
  }

  /** Get VM attribute schema for dynamic table columns/detail rows */
  async getVmsSchema(): Promise<VmSchemaAttribute[]> {
    return this.request<VmSchemaAttribute[]>('GET', '/vms/schema');
  }

  /** Get a single VM by ID */
  async getVm(id: number): Promise<Vm> {
    return this.request<Vm>('GET', `/vm/${id}`);
  }

  /** Create a new VM */
  async createVm(vm: VmCreateRequest): Promise<Vm> {
    const result = await this.request<{ created: Vm }>('POST', '/vms', vm);
    return result.created;
  }

  /** Update a VM */
  async updateVm(id: number, updates: VmUpdateRequest): Promise<Vm> {
    const result = await this.request<{ vm: Vm }>('PUT', `/vm/${id}`, updates);
    return result.vm;
  }

  /** Delete a VM */
  async deleteVm(id: number): Promise<void> {
    await this.request<void>('DELETE', `/vm/${id}`);
  }

  /** Start a VM (optionally with a LUKS passphrase for encrypted disks) */
  async startVm(id: number, body?: { passphrase?: string; store_passphrase?: boolean }): Promise<{ message: string }> {
    return this.request<{ message: string }>('POST', `/vm/${id}/start`, body);
  }

  /** Store an encrypted passphrase for a VM in the keystore */
  async storePassphrase(vmId: number, passphrase: string): Promise<{ stored: boolean }> {
    return this.request<{ stored: boolean }>('POST', `/vm/${vmId}/passphrase`, { passphrase });
  }

  /** Remove a stored passphrase for a VM from the keystore */
  async removePassphrase(vmId: number): Promise<{ removed: boolean }> {
    return this.request<{ removed: boolean }>('DELETE', `/vm/${vmId}/passphrase`);
  }

  /** Stop a VM gracefully */
  async stopVm(id: number, timeout?: number): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      'POST',
      `/vm/${id}/stop`,
      { force_kill: false, ...(timeout ? { timeout } : {}) }
    );
  }

  /** Force kill a VM */
  async killVm(id: number): Promise<{ message: string }> {
    return this.request<{ message: string }>('POST', `/vm/${id}/kill`);
  }

  /** Pause a VM */
  async pauseVm(id: number): Promise<{ message: string }> {
    return this.request<{ message: string }>('POST', `/vm/${id}/pause`);
  }

  /** Resume a paused VM */
  async resumeVm(id: number): Promise<{ message: string }> {
    return this.request<{ message: string }>('POST', `/vm/${id}/resume`);
  }

  /** Clear UEFI NVRAM variables (VM must be stopped) */
  async clearUefiVars(id: number): Promise<{ status: string }> {
    return this.request<{ status: string }>('POST', `/vm/${id}/clear-uefi-vars`);
  }

  /** Get VM runtime status */
  async getVmStatus(id: number): Promise<VmStatusResponse> {
    return this.request<VmStatusResponse>('GET', `/vm/${id}/status`);
  }

  /** Get VM VNC connection info (QEMU display) */
  async getVmVnc(id: number): Promise<VmVncInfo> {
    return this.request<VmVncInfo>('GET', `/vm/${id}/vnc`);
  }

  /** Get guest VNC connection info (VNC server inside guest) */
  async getVmGuestVnc(id: number): Promise<VmGuestVncInfo> {
    return this.request<VmGuestVncInfo>('GET', `/vm/${id}/guest-vnc`);
  }

  /** Connect to VM via RDP (sets up port mapping) */
  async connectVmRdp(id: number): Promise<VmRdpInfo> {
    return this.request<VmRdpInfo>('POST', `/vm/${id}/rdp`);
  }

  /** Get the download URL for an RDP file */
  getVmRdpDownloadUrl(id: number): string {
    return `${this.baseUrl}/vm/${id}/rdp/download`;
  }

  // ---------------------------------------------------------------------------
  // Port Forward Endpoints
  // ---------------------------------------------------------------------------

  /** List active port forwards for a VM */
  async listVmPortForwards(id: number): Promise<PortForwardInfo[]> {
    const result = await this.request<{ port_forwards: PortForwardInfo[] }>(
      'GET', `/vm/${id}/portfwd`);
    return result.port_forwards;
  }

  /** Create a port forward for a VM */
  async createVmPortForward(id: number, data: CreatePortForwardRequest): Promise<PortForwardInfo> {
    return this.request<PortForwardInfo>('POST', `/vm/${id}/portfwd`, data);
  }

  /** Delete a port forward by host port */
  async deleteVmPortForward(id: number, hostPort: number): Promise<void> {
    await this.request<unknown>('DELETE', `/vm/${id}/portfwd/${hostPort}`);
  }

  // ---------------------------------------------------------------------------
  // Monitoring Endpoints
  // ---------------------------------------------------------------------------

  /** Get all VMs monitoring info */
  async getMonitorVms(): Promise<VmMonitorInfo[]> {
    const result = await this.request<{ vms: VmMonitorInfo[] }>('GET', '/vms/monitor');
    return result.vms;
  }

  /** Get monitoring info for a specific VM */
  async getMonitorVm(id: number): Promise<VmMonitorInfo> {
    return this.request<VmMonitorInfo>('GET', `/vm/${id}/monitor`);
  }

  /** Get host metrics */
  async getMonitorHost(): Promise<HostMetrics> {
    return this.request<HostMetrics>('GET', '/monitor/host');
  }

  /** Get monitoring events */
  async getMonitorEvents(options?: {
    since_id?: number;
    type?: string;
    vm_id?: number;
    limit?: number;
  }): Promise<MonitorEventsResponse> {
    return this.request<MonitorEventsResponse>('GET', '/monitor/events', undefined, options);
  }

  // ---------------------------------------------------------------------------
  // Storage Endpoints
  // ---------------------------------------------------------------------------

  /** List ISO images */
  async listIsoImages(host?: string): Promise<IsoImage[]> {
    const result = await this.request<{ images: IsoImage[] }>('GET', '/storage/iso-images', undefined, host ? { host } : undefined);
    return result.images;
  }

  /** List disk images */
  async listDisks(host?: string): Promise<DiskInfo[]> {
    const result = await this.request<{ disks: DiskInfo[] }>('GET', '/storage/disks', undefined, host ? { host } : undefined);
    return result.disks;
  }

  /** List storage pools */
  async listStoragePools(host?: string): Promise<StoragePool[]> {
    const result = await this.request<{ pools: StoragePool[] }>('GET', '/storage/pools', undefined, host ? { host } : undefined);
    return result.pools;
  }

  /** Create a new disk image */
  async createDisk(request: CreateDiskRequest, host?: string): Promise<{ path: string; size_gb: number; format: string; encrypted?: boolean }> {
    return this.request<{ path: string; size_gb: number; format: string; encrypted?: boolean }>('POST', '/storage/disks', request, host ? { host } : undefined);
  }

  /** Create a new storage pool */
  async createStoragePool(path: string, name?: string, host?: string): Promise<StoragePool> {
    const body: { path: string; name?: string } = { path };
    if (name) body.name = name;
    return this.request<StoragePool>('POST', '/storage/pools', body, host ? { host } : undefined);
  }

  /** Delete a storage pool */
  async deleteStoragePool(name: string, host?: string): Promise<{ deleted: string }> {
    return this.request<{ deleted: string }>('DELETE', `/storage/pools/${encodeURIComponent(name)}`, undefined, host ? { host } : undefined);
  }

  /** Clone a disk image */
  async cloneDisk(
    storage: string, image: string, name: string,
    pool?: string, fullCopy?: boolean, host?: string, base?: boolean,
    sourceBase?: boolean, passphrase?: string, newPassphrase?: string
  ): Promise<DiskInfo> {
    const body: { name: string; pool?: string; full_copy?: boolean; base?: boolean; source_base?: boolean; passphrase?: string; new_passphrase?: string } = { name };
    if (pool) body.pool = pool;
    if (fullCopy !== undefined) body.full_copy = fullCopy;
    if (base) body.base = true;
    if (sourceBase) body.source_base = true;
    if (passphrase) body.passphrase = passphrase;
    if (newPassphrase) body.new_passphrase = newPassphrase;
    return this.request<DiskInfo>('POST', `/storage/disks/${encodeURIComponent(storage)}/${encodeURIComponent(image)}/clone`, body, host ? { host } : undefined);
  }

  /** Resize a disk image */
  async resizeDisk(storage: string, image: string, newSizeGb: number, host?: string, base?: boolean): Promise<{ path: string; new_size_gb: number }> {
    const body: { new_size_gb: number; base?: boolean } = { new_size_gb: newSizeGb };
    if (base) body.base = true;
    return this.request<{ path: string; new_size_gb: number }>(
      'POST',
      `/storage/disks/${encodeURIComponent(storage)}/${encodeURIComponent(image)}/resize`,
      body,
      host ? { host } : undefined
    );
  }

  /** Stat a disk file (for clone progress tracking) */
  async getDiskStat(pool: string, name: string, host?: string, base?: boolean): Promise<{ exists: boolean; size_bytes: number }> {
    const params: Record<string, string> = {};
    if (host) params.host = host;
    if (base) params.base = 'true';
    return this.request<{ exists: boolean; size_bytes: number }>(
      'GET',
      `/storage/disks/${encodeURIComponent(pool)}/${encodeURIComponent(name)}/stat`,
      undefined, params
    );
  }

  /** List physical drives */
  async listPhysicalDrives(host?: string): Promise<PhysicalDrive[]> {
    const result = await this.request<{ drives: PhysicalDrive[] }>('GET', '/storage/drives', undefined, host ? { host } : undefined);
    return result.drives;
  }

  // ---------------------------------------------------------------------------
  // Auth Endpoints
  // ---------------------------------------------------------------------------

  /** Try proxy-based login (X-Auth-User header from trusted reverse proxy) */
  async proxyLogin(): Promise<LoginResponse> {
    const result = await this.request<LoginResponse>('POST', '/auth/proxy-login');
    if (result.session_token) {
      this.saveToken(result.session_token);
    }
    return result;
  }

  /** Login with username and password */
  async login(
    username: string,
    password: string,
    deviceToken?: string
  ): Promise<LoginResponse> {
    const request: LoginRequest = { username, password };
    if (deviceToken) {
      request.device_token = deviceToken;
    }
    const result = await this.request<LoginResponse>('POST', '/auth/login', request);
    if (result.session_token && !result.requires_2fa) {
      this.saveToken(result.session_token);
    }
    return result;
  }

  /** Verify 2FA code */
  async verify2fa(code: string, trustDevice?: boolean): Promise<Verify2faResponse> {
    const pendingToken = sessionStorage.getItem('pending_token') || '';
    const request: Verify2faRequest = { pending_token: pendingToken, code };
    if (trustDevice !== undefined) {
      request.trust_device = trustDevice;
    }
    const result = await this.request<Verify2faResponse>('POST', '/auth/verify', request);
    if (result.session_token) {
      this.saveToken(result.session_token);
    }
    if (trustDevice && result.device_token) {
      localStorage.setItem('vmsman_device_token', result.device_token);
    }
    return result;
  }

  /** Resend 2FA verification code */
  async resendCode(): Promise<LoginResponse> {
    const pendingToken = sessionStorage.getItem('pending_token') || '';
    const result = await this.request<LoginResponse>('POST', '/auth/resend', { pending_token: pendingToken });
    if (result.pending_token) {
      sessionStorage.setItem('pending_token', result.pending_token);
    }
    return result;
  }

  /** Request a password reset email */
  async requestPasswordReset(email: string): Promise<void> {
    await this.request<void>('POST', '/auth/request-reset', { email });
  }

  /** Reset password with token */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    await this.request<void>('POST', '/auth/reset-password', {
      token,
      new_password: newPassword,
    });
  }

  /** Check if an invite token is valid without consuming it */
  async checkInvite(token: string): Promise<{ valid: boolean; reason?: string }> {
    return this.request<{ valid: boolean; reason?: string }>('POST', '/auth/check-invite', { token });
  }

  /** Complete invite (set password for new user) */
  async completeInvite(token: string, password: string): Promise<void> {
    await this.request<void>('POST', '/auth/complete-invite', {
      token,
      password,
    });
  }

  /** Logout */
  async logout(): Promise<void> {
    await this.request<void>('POST', '/auth/logout');
    this.saveToken(null);
  }

  /** Initiate TOTP setup — returns secret and otpauth URI */
  async setupTotp(): Promise<TotpSetupResponse> {
    return this.request<TotpSetupResponse>('POST', '/auth/totp/setup');
  }

  /** Confirm TOTP enrollment with a 6-digit verification code */
  async confirmTotp(code: string): Promise<void> {
    await this.request<void>('POST', '/auth/totp/confirm', { code });
  }

  /** Disable TOTP for the current user */
  async disableTotp(): Promise<void> {
    await this.request<void>('DELETE', '/auth/totp');
  }

  /** Get current user info (uses preloaded fetch if available) */
  async getCurrentUser(): Promise<UserInfo> {
    // Check for preloaded /api/auth/me fetch started in <head>
    const preload = (window as unknown as Record<string, unknown>).__preload_user as Promise<Response> | undefined;
    let raw: Record<string, unknown>;
    if (preload) {
      delete (window as unknown as Record<string, unknown>).__preload_user;
      const resp = await preload;
      if (!resp.ok) {
        if (resp.status === 401 || resp.status === 403) {
          this.onAuthError?.();
        }
        const text = await resp.text().catch(() => '');
        throw new ApiClientError('API_ERROR', text || resp.statusText, resp.status);
      }
      raw = await resp.json() as Record<string, unknown>;
    } else {
      raw = await this.request<Record<string, unknown>>('GET', '/auth/me');
    }

    // Use backend role directly
    const role = ((raw.role as string) || 'readonly') as UserInfo['role'];

    // Build permissions list based on role (matches backend auth levels)
    const permissions: string[] = ['view-vms', 'view-resources'];
    if (role === 'operator') {
      permissions.push('control-vms', 'manage-users');
    }
    if (role === 'admin') {
      permissions.push('control-vms', 'create-vms', 'manage-disks',
                        'manage-users', 'manage-storage', 'admin');
    }

    return {
      username: raw.username as string,
      email: raw.email as string | undefined,
      totp_enabled: raw.totp_enabled as boolean,
      role,
      permissions,
      settings: raw.settings as Record<string, unknown> | undefined,
      organizations: raw.organizations as string[] | undefined,
    };
  }

  /** List trusted devices */
  async listDevices(): Promise<DeviceInfo[]> {
    const result = await this.request<{ devices: DeviceInfo[] }>('GET', '/auth/devices');
    return result.devices;
  }

  /** Revoke a device */
  async revokeDevice(tokenPrefix: string): Promise<void> {
    await this.request<void>('DELETE', `/auth/devices/${encodeURIComponent(tokenPrefix)}`);
  }

  /** Revoke all devices except current */
  async revokeAllDevices(): Promise<void> {
    await this.request<void>('DELETE', '/auth/devices/all');
  }

  // ---------------------------------------------------------------------------
  // User Management Endpoints (Admin)
  // ---------------------------------------------------------------------------

  /** List all users */
  async listUsers(): Promise<User[]> {
    const result = await this.request<ListUsersResponse>('GET', '/users');
    return result.users;
  }

  /** List all users with metadata (includes available AD domains) */
  async listUsersWithMeta(): Promise<ListUsersResponse> {
    return this.request<ListUsersResponse>('GET', '/users');
  }

  /** Get a user by username */
  async getUser(username: string): Promise<User> {
    return this.request<User>('GET', `/users/${encodeURIComponent(username)}`);
  }

  /** Create a new user */
  async createUser(request: CreateUserRequest): Promise<User> {
    return this.request<User>('POST', '/users', request);
  }

  /** Update a user */
  async updateUser(username: string, updates: UpdateUserRequest): Promise<User> {
    return this.request<User>('PUT', `/users/${encodeURIComponent(username)}`, updates);
  }

  /** Delete a user */
  async deleteUser(username: string): Promise<void> {
    await this.request<void>('DELETE', `/users/${encodeURIComponent(username)}`);
  }

  /** Change user password */
  async changePassword(username: string, request: ChangePasswordRequest): Promise<void> {
    await this.request<void>('POST', `/users/${encodeURIComponent(username)}/password`, request);
  }

  // ---------------------------------------------------------------------------
  // Options/Config Endpoints
  // ---------------------------------------------------------------------------

  /** Get API options (for form dropdowns) */
  async getOptions(): Promise<ApiOptions> {
    return this.request<ApiOptions>('GET', '/options');
  }

  /** Get host information */
  async getHostInfo(): Promise<HostInfo> {
    return this.request<HostInfo>('GET', '/host');
  }

  // ---------------------------------------------------------------------------
  // Remote Hosts Management
  // ---------------------------------------------------------------------------

  /** List all hosts (local + remote) */
  async listHosts(): Promise<HostsListResponse> {
    return this.request<HostsListResponse>('GET', '/hosts');
  }

  /** Get a specific remote host */
  async getRemoteHost(name: string): Promise<RemoteHost> {
    return this.request<RemoteHost>('GET', `/hosts/${encodeURIComponent(name)}`);
  }

  /** Create a new remote host */
  async createRemoteHost(host: RemoteHostRequest & { name: string }): Promise<{ created: RemoteHost }> {
    return this.request<{ created: RemoteHost }>('POST', '/hosts', host);
  }

  /** Update a remote host */
  async updateRemoteHost(name: string, updates: RemoteHostRequest): Promise<{ updated: RemoteHost }> {
    return this.request<{ updated: RemoteHost }>('PUT', `/hosts/${encodeURIComponent(name)}`, updates);
  }

  /** Delete a remote host */
  async deleteRemoteHost(name: string): Promise<{ deleted: string }> {
    return this.request<{ deleted: string }>('DELETE', `/hosts/${encodeURIComponent(name)}`);
  }

  /** Test connectivity to a remote host */
  async testRemoteHost(name: string): Promise<HostTestResult> {
    return this.request<HostTestResult>('POST', `/hosts/${encodeURIComponent(name)}/test`);
  }

  /** Get remote hosts as a Record for dropdowns (name -> display label) */
  async getHostsForDropdown(): Promise<Record<string, string>> {
    const response = await this.listHosts();
    const result: Record<string, string> = {};
    // Add local host (empty string or null means local)
    result[''] = `${response.local.name || 'local'} (local)`;
    // Add remote hosts
    for (const host of response.hosts) {
      result[host.name] = host.name;
    }
    return result;
  }

  // ---------------------------------------------------------------------------
  // Organizations Management
  // ---------------------------------------------------------------------------

  /** List all organizations */
  async listOrganizations(): Promise<OrganizationsListResponse> {
    return this.request<OrganizationsListResponse>('GET', '/organizations');
  }

  /** Get a specific organization */
  async getOrganization(name: string): Promise<Organization> {
    return this.request<Organization>('GET', `/organizations/${encodeURIComponent(name)}`);
  }

  /** Create a new organization (with optional network + initial user provisioning) */
  async createOrganization(org: CreateOrganizationRequest): Promise<{ created: Organization; initial_user?: unknown }> {
    return this.request<{ created: Organization; initial_user?: unknown }>('POST', '/organizations', org);
  }

  /** Update an organization */
  async updateOrganization(name: string, updates: Partial<Organization>): Promise<{ updated: Organization }> {
    return this.request<{ updated: Organization }>('PUT', `/organizations/${encodeURIComponent(name)}`, updates);
  }

  /** Delete an organization */
  async deleteOrganization(name: string): Promise<{ deleted: string }> {
    return this.request<{ deleted: string }>('DELETE', `/organizations/${encodeURIComponent(name)}`);
  }

  /** Get current organization (resolved from vhost or default) */
  async getCurrentOrganization(): Promise<Organization | null> {
    const result = await this.request<Organization>('GET', '/organization');
    if (!result || !result.name) return null;
    return result;
  }

  // ---------------------------------------------------------------------------
  // Templates Management
  // ---------------------------------------------------------------------------

  /** List all templates */
  async listTemplates(): Promise<VmTemplate[]> {
    const r = await this.request<TemplatesListResponse>('GET', '/templates');
    return r.templates;
  }

  /** Get a specific template */
  async getTemplate(name: string): Promise<VmTemplate> {
    return this.request<VmTemplate>('GET', `/templates/${encodeURIComponent(name)}`);
  }

  /** Create a new template */
  async createTemplate(template: VmTemplate): Promise<{ created: VmTemplate }> {
    return this.request<{ created: VmTemplate }>('POST', '/templates', template);
  }

  /** Update a template */
  async updateTemplate(name: string, updates: Partial<VmTemplate>): Promise<{ updated: VmTemplate }> {
    return this.request<{ updated: VmTemplate }>('PUT', `/templates/${encodeURIComponent(name)}`, updates);
  }

  /** Delete a template */
  async deleteTemplate(name: string): Promise<{ deleted: string }> {
    return this.request<{ deleted: string }>('DELETE', `/templates/${encodeURIComponent(name)}`);
  }

  /** Get server version (unauthenticated) */
  async getVersion(): Promise<{ version: string }> {
    return this.request<{ version: string }>('GET', '/version');
  }

  /** Reload configuration */
  async reloadConfig(): Promise<{ message: string }> {
    return this.request<{ message: string }>('POST', '/reload');
  }

  /** Import running VMs */
  async importRunningVms(): Promise<{ imported: number }> {
    return this.request<{ imported: number }>('POST', '/import');
  }

  /**
   * List network bridges (read-only). Networks are the operator surface;
   * this is used to show the managed vmsman-br (for a network's uplink
   * candidates) and externally-managed bridges (for direct VM attach).
   */
  async getNetBridges(host?: string): Promise<BridgeDetail[]> {
    const result = await this.request<{ bridges: BridgeDetail[] }>('GET', '/net/bridges', undefined, host ? { host } : undefined);
    return result.bridges;
  }

  /** Get Samba AD DC config for a bridge */
  async getBridgeSamba(name: string, host?: string): Promise<BridgeSambaConfig> {
    return this.request<BridgeSambaConfig>('GET', `/net/bridges/${encodeURIComponent(name)}/samba`, undefined, host ? { host } : undefined);
  }

  /** Update Samba AD DC config for a bridge */
  async updateBridgeSamba(name: string, config: Partial<BridgeSambaConfig>, host?: string): Promise<BridgeSambaConfig> {
    return this.request<BridgeSambaConfig>('PUT', `/net/bridges/${encodeURIComponent(name)}/samba`, config, host ? { host } : undefined);
  }

  /** List configured [network.X] entries */
  async getNetNetworks(host?: string): Promise<NetworkDetail[]> {
    const result = await this.request<{ networks: NetworkDetail[] }>('GET', '/net/networks', undefined, host ? { host } : undefined);
    return result.networks;
  }

  /** Get a single network by name */
  async getNetwork(name: string, host?: string): Promise<NetworkDetail> {
    return this.request<NetworkDetail>('GET', `/net/networks/${encodeURIComponent(name)}`, undefined, host ? { host } : undefined);
  }

  /** Create a new [network.X] entry */
  async createNetwork(network: CreateNetworkRequest, host?: string): Promise<{ created: NetworkDetail }> {
    return this.request<{ created: NetworkDetail }>('POST', '/net/networks', network, host ? { host } : undefined);
  }

  /** Update an existing [network.X] entry */
  async updateNetwork(name: string, updates: Partial<NetworkDetail>, host?: string): Promise<{ updated: NetworkDetail }> {
    return this.request<{ updated: NetworkDetail }>('PUT', `/net/networks/${encodeURIComponent(name)}`, updates, host ? { host } : undefined);
  }

  /** Delete a [network.X] entry */
  async deleteNetwork(name: string, host?: string): Promise<{ deleted: string }> {
    return this.request<{ deleted: string }>('DELETE', `/net/networks/${encodeURIComponent(name)}`, undefined, host ? { host } : undefined);
  }

  /** List available network interfaces */
  async getNetInterfaces(host?: string): Promise<string[]> {
    const result = await this.request<{ interfaces: string[] }>('GET', '/net/interfaces', undefined, host ? { host } : undefined);
    return result.interfaces;
  }

  /**
   * Subset of network interfaces that currently have default routes.
   * Useful for populating NAT-uplink-style pickers where "which iface
   * reaches the internet" is the relevant filter. May be empty on a
   * host with no upstream connectivity.
   */
  async getDefaultGatewayInterfaces(host?: string): Promise<string[]> {
    const result = await this.request<{ default_gateway_interfaces?: string[] }>(
      'GET', '/net/interfaces', undefined, host ? { host } : undefined,
    );
    return result.default_gateway_interfaces || [];
  }

  /** Apply network configuration (create/update bridges) */
  async applyNetworks(host?: string): Promise<{ status: string }> {
    return this.request<{ status: string }>('POST', '/net/apply', undefined, host ? { host } : undefined);
  }

  /** Get VPN configuration */
  async getVpnConfig(): Promise<{ download_url?: string; instructions?: string }> {
    return this.request<{ download_url?: string; instructions?: string }>('GET', '/vpn/config');
  }

  /** Start all VMs */
  async startAllVms(): Promise<void> {
    await this.request<void>('POST', '/vms/start-all');
  }

  /** Stop all VMs */
  async stopAllVms(): Promise<void> {
    await this.request<void>('POST', '/vms/stop-all', { force_kill: false });
  }

  // ---------------------------------------------------------------------------
  // Convenience Methods (for form dropdowns)
  // ---------------------------------------------------------------------------

  /** Get ISO images as a Record for dropdowns */
  async getIsoImages(host?: string): Promise<Record<string, string>> {
    const images = await this.listIsoImages(host);
    const result: Record<string, string> = { none: 'None' };
    for (const img of images) {
      // Backend strips path — cdrom mount resolves by filename
      const key = img.path || img.name;
      result[key] = img.name;
    }
    return result;
  }

  /** Get secondary disks as a Record for dropdowns */
  async getSecondaryDisks(host?: string): Promise<Record<string, string>> {
    const disks = await this.listDisks(host);
    const result: Record<string, string> = { none: 'None' };
    for (const disk of disks) {
      result[disk.path] = disk.name || disk.path;
    }
    return result;
  }

  /** Get storage pools as a Record for dropdowns (keyed by pool name) */
  async getStoragePools(host?: string): Promise<Record<string, unknown>> {
    const pools = await this.listStoragePools(host);
    const result: Record<string, unknown> = {};
    for (const pool of pools) {
      const total = pool.total_bytes || 0;
      const avail = pool.available_bytes || 0;
      const used_pct = total > 0 ? Math.round(((total - avail) / total) * 100) : 0;
      result[pool.name] = {
        label: pool.name,
        avail_human: total > 0 ? formatBytes(avail) : 'N/A',
        size_human: total > 0 ? formatBytes(total) : 'N/A',
        used_pct,
      };
    }
    return result;
  }

  /** Get Hyper-V enlightenments from options */
  async getHvEnlightenments(): Promise<Record<string, string>> {
    const options = await this.getOptions();
    return options.hv_enlightenments || {};
  }

  /** Get Hyper-V enlightenment sets from options */
  async getHvEnlightenmentSets(): Promise<Record<string, string[]>> {
    const options = await this.getOptions();
    return options.hv_enlightenment_sets || {};
  }

  /** Get available VM services from options */
  async getServices(): Promise<Record<string, string>> {
    const options = await this.getOptions();
    return options.services || {};
  }

  /** Get drive interfaces from options */
  async getDriveInterfaces(): Promise<Record<string, string>> {
    const options = await this.getOptions();
    return options.drive_interfaces || {};
  }

  /** Get drive cache modes from options */
  async getDriveCacheModes(): Promise<Record<string, string>> {
    const options = await this.getOptions();
    return options.drive_cache_modes || {};
  }

  /** Get drive AIO modes from options */
  async getDriveAioModes(): Promise<Record<string, string>> {
    const options = await this.getOptions();
    return options.drive_aio_modes || {};
  }

  /** Get drive formats from options */
  async getDriveFormats(): Promise<Record<string, string>> {
    const options = await this.getOptions();
    return options.drive_formats || {};
  }

  /** Get CPU models from options */
  async getCpuModels(): Promise<Record<string, string>> {
    const options = await this.getOptions();
    return options.cpu_models || {};
  }

  /** Get CPU flags from options */
  async getCpuFlags(): Promise<Record<string, string>> {
    const options = await this.getOptions();
    return options.cpu_flags || {};
  }

  // ---------------------------------------------------------------------------
  // VM Create/Update with params object
  // ---------------------------------------------------------------------------

  /** Create or update a VM (used by modify dialog) */
  async createOrUpdateVm(params: Record<string, unknown>): Promise<Vm> {
    if (params.id !== undefined) {
      // Update existing VM
      return this.updateVm(params.id as number, params as unknown as VmUpdateRequest);
    } else {
      // Create new VM
      return this.createVm(params as unknown as VmCreateRequest);
    }
  }

  // ---------------------------------------------------------------------------
  // USB Device Management
  // ---------------------------------------------------------------------------

  /** Get host USB devices */
  async getHostUsbDevices(host?: string): Promise<unknown[]> {
    const result = await this.request<{ devices: unknown[] }>('GET', '/host/usb', undefined, host ? { host } : undefined);
    return result.devices || [];
  }

  /** Get host PCI devices */
  async getHostPciDevices(host?: string): Promise<import('./types').HostPciDevice[]> {
    const result = await this.request<{ devices: import('./types').HostPciDevice[] }>('GET', '/host/pci', undefined, host ? { host } : undefined);
    return result.devices || [];
  }

  /** Attach a USB device to a VM */
  async attachUsbDevice(
    vmId: number,
    vendorId: string,
    productId: string,
    description?: string
  ): Promise<void> {
    await this.request<void>('POST', `/vm/${vmId}/usb`, {
      vendor_id: vendorId,
      product_id: productId,
      description,
    });
  }

  /** Detach a USB device from a VM */
  async detachUsbDevice(vmId: number, vendorId: string, productId: string): Promise<void> {
    await this.request<void>('DELETE', `/vm/${vmId}/usb`, {
      vendor_id: vendorId,
      product_id: productId,
    });
  }

  // ---------------------------------------------------------------------------
  // CD-ROM Management
  // ---------------------------------------------------------------------------

  /** Mount an ISO image to a running VM's CD-ROM drive */
  async mountCdrom(id: number, iso: string): Promise<void> {
    await this.request<void>('POST', `/vm/${id}/cdrom/${encodeURIComponent(iso)}`);
  }

  // ---------------------------------------------------------------------------
  // Drive Hotplug
  // ---------------------------------------------------------------------------

  /** Hotplug a drive to a running VM */
  async hotplugDrive(
    vmId: number,
    drive: { path: string; interface?: string; readonly?: boolean }
  ): Promise<void> {
    await this.request<void>('POST', `/vm/${vmId}/drives`, drive);
  }

  /** Hot-unplug a drive from a running VM */
  async hotunplugDrive(vmId: number, driveIndex: number): Promise<void> {
    await this.request<void>('DELETE', `/vm/${vmId}/drives/${driveIndex}`);
  }

  // ---------------------------------------------------------------------------
  // User Settings
  // ---------------------------------------------------------------------------

  /** Get current user's settings */
  async getUserSettings(): Promise<Record<string, unknown>> {
    const user = await this.getCurrentUser();
    return user.settings || {};
  }

  /** Update current user's settings */
  async updateUserSettings(data: { settings: Record<string, unknown> }): Promise<void> {
    await this.request<void>('PUT', '/auth/me', data);
  }

  /** Set password for a user */
  async setUserPassword(username: string, password: string): Promise<void> {
    await this.changePassword(username, { password });
  }

  // ---------------------------------------------------------------------------
  // Admin User Management
  // ---------------------------------------------------------------------------

  /** Get all users (admin) */
  async getUsers(): Promise<User[]> {
    return this.listUsers();
  }

  /** Send user invite email */
  async sendUserInvite(username: string): Promise<void> {
    await this.request<void>('POST', `/users/${encodeURIComponent(username)}/invite`);
  }

  /** Admin reset password (sends reset email) */
  async adminResetPassword(username: string): Promise<void> {
    await this.request<void>('POST', `/users/${encodeURIComponent(username)}/reset-password`);
  }

  /** Generate a new API key for a user (replaces any existing key) */
  async generateApiKey(username: string): Promise<GenerateApiKeyResponse> {
    return this.request<GenerateApiKeyResponse>('POST', `/users/${encodeURIComponent(username)}/api-key`);
  }

  /** Revoke a user's API key */
  async revokeApiKey(username: string): Promise<void> {
    await this.request<void>('DELETE', `/users/${encodeURIComponent(username)}/api-key`);
  }

  // ---------------------------------------------------------------------------
  // Admin Settings
  // ---------------------------------------------------------------------------

  /** Get admin settings with schema and performance presets */
  async getAdminSettings(): Promise<{
    schema: unknown;
    values: Record<string, unknown>;
    presets?: Record<string, Record<string, unknown>>;
  }> {
    return this.request<{
      schema: unknown;
      values: Record<string, unknown>;
      presets?: Record<string, Record<string, unknown>>;
    }>('GET', '/admin/settings');
  }

  /** Save admin settings */
  async setAdminSettings(data: { settings: Record<string, unknown> }): Promise<void> {
    await this.request<void>('PUT', '/admin/settings', data);
  }

  // ---------------------------------------------------------------------------
  // Host Metrics (for resource bars)
  // ---------------------------------------------------------------------------

  /** Get host metrics in format expected by resource bars */
  async getHostMetrics(): Promise<unknown> {
    return this.request<unknown>('GET', '/monitor/host');
  }

  /**
   * Verify the current session is still valid.
   * Returns false (and triggers onAuthError redirect) if the session has expired.
   */
  async verifySession(): Promise<boolean> {
    if (!this.sessionToken) return false;
    try {
      await this.request<unknown>('GET', '/auth/me');
      return true;
    } catch {
      return false;
    }
  }
}

// =============================================================================
// Error Class
// =============================================================================

export class ApiClientError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status?: number,
    public readonly details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiClientError';
  }

  isAuthError(): boolean {
    return this.status === 401;
  }

  isNotFound(): boolean {
    return this.status === 404;
  }

  isValidationError(): boolean {
    return this.code === 'VALIDATION_ERROR' || this.status === 400;
  }

  isConflict(): boolean {
    return this.status === 409;
  }
}

// =============================================================================
// Default Client Instance
// =============================================================================

export const api = new ApiClient({
  onAuthError: () => {
    // Redirect to login on auth error
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  },
});
